import React, { useEffect } from "react";
import classNames from "classnames";
import { Field, Form } from "react-final-form";
import useValidationSchema from "hooks/useValidationSchema";
import { Button, Modal, Tooltip, ToolTipPosition } from "@pickit/pickit-components";
import "./editPreliquidationAmount.scss";
import { EditPreliquidationAmountProps } from "./types";
import { formatDDMMYYYY } from "utils/date";
import { Input } from "component/inputs/Input";
import { NumericInput } from "component/inputs/NumericInput";
import { ReactComponent as PlusIcon } from "../../../assets/icon_plus.svg";
import { ReactComponent as MinusIcon } from "../../../assets/icon_minus.svg";
import { LoadingButtonState } from "component/loadingButton/types";
import { PRELIQUIDATION_STATUS_NAMES, VALIDATION_REGEX } from "utils/constants";
import "./i18n";
import { useTranslation } from "react-i18next";

export const EditPreliquidationAmount: React.FC<
  EditPreliquidationAmountProps
> = ({
  formRef,
  increase,
  preliquidation,
  initialValues,
  validationSchema,
  adjustingAmount,
  isOpen,
  testId,
  setIncrease,
  onSubmit,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const onClick = () => {
    if (adjustingAmount > LoadingButtonState.Loading) {
      if (adjustingAmount === LoadingButtonState.Success) {
        if (formRef.current) {
          formRef.current.restart();
        }
        onClose(true, false);
      } else {
        const isDirty = formRef.current?.getState().dirty || false;
        onClose(true, isDirty);
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'modify-amount');
    } else {
      params.delete('modal');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);
  
  return (
    <Modal
      width="1190px"
      height="496px"
      isOpen={isOpen}
      onClose={() => {
        const isDirty = formRef.current?.getState().dirty || false;
        onClose(adjustingAmount > LoadingButtonState.Loading, isDirty);
      }}
      closeOnBlur={false}
      testId={testId}
    >
      <div className="preliquidation-modal-container">
        <div className="preliquidation-modal-header">
          <div className="preliquidation-modal-header-number">
            <label data-testId={`${testId}-field-preliquidation-number`}>{t("editPresettlement:preliquidationNumber")}</label>
            <p data-testId={`${testId}-field-preliquidation-number-value`}>{preliquidation.id}</p>
          </div>
          <div className="preliquidation-modal-header-status">
            <label data-testId={`${testId}-field-status`}>{t("editPresettlement:status")}</label>
            <p data-testId={`${testId}-field-status-value`}>{t(`${PRELIQUIDATION_STATUS_NAMES[preliquidation.status]}`)}</p>
          </div>
          <div className="preliquidation-modal-header-date">
            <label data-testId={`${testId}-field-date-of-issue`}>{t("editPresettlement:dateOfIssue")}</label>
            {preliquidation.generatedAt && (
              <p data-testId={`${testId}-field-date-of-issue-value`}>{formatDDMMYYYY(preliquidation.generatedAt)}</p>
            )}
          </div>
        </div>
        <div className="preliquidation-modal-content">
          <h6 className="preliquidation-modal-title" data-testId={`${testId}-title`}>
            {t("editPresettlement:instruction")}
          </h6>
          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            validate={useValidationSchema(validationSchema)}
          >
            {({ invalid, form, handleSubmit, dirty }) => {
              formRef.current = form;
              return (
                <form
                  className="preliquidation-modal-form"
                  onSubmit={handleSubmit}
                >
                  <div className="preliquidation-modal-form-row">
                    <Field
                      readOnly
                      type="text"
                      name="actualAmount"
                      label={t("editPresettlement:actualAmount")}
                      component={Input}
                      className="Admin-Pickers-input"
                      placeholder={t("editPresettlement:actualAmount")}
                      testId={`${testId}-field-actual-amount`}
                      id={`${testId}-field-actual-amount-input`}
                    />
                    <div className="preliquidation-modal-buttons">
                      <Tooltip
                        message={t("editPresettlement:add")}
                        position={ToolTipPosition.top}
                      >
                        <PlusIcon
                          onClick={() => setIncrease(true)}
                          className={classNames({ disabled: !increase })}
                          data-testId={`${testId}-button-add`}
                        />
                      </Tooltip>
                      <Tooltip
                        message={t("editPresettlement:subtract")}
                        position={ToolTipPosition.top}
                      >
                        <div>
                          <MinusIcon
                            onClick={() => setIncrease(false)}
                            className={classNames({ disabled: increase })}
                            data-testId={`${testId}-button-subtract`}
                          />
                        </div>
                      </Tooltip>
                    </div>
                    <Field
                      name="newAmount"
                      label={t(
                        `editPresettlement:${
                          increase ? "amountToAdd" : "amountToSubstract"
                        }`
                      )}
                      component={NumericInput}
                      maxDecimal={2}
                      className="Admin-Pickers-input"
                      placeholder={t("editPresettlement:amount")}
                      testId={`${testId}-field-new-amount`}
                      id={`${testId}-field-new-amount-input`}
                    />
                  </div>
                  <Field
                    type="text"
                    name="reason"
                    label={t("editPresettlement:reasonLabel")}
                    component={Input}
                    className="Admin-Pickers-input"
                    placeholder={t("editPresettlement:reasonPlaceholder")}
                    pattern={VALIDATION_REGEX.regStreet}
                    maxLength={150}
                    testId={`${testId}-field-reason`}
                    id={`${testId}-field-reason-input`}
                  />
                  <div className="button-confirm-container">
                    <Button
                      testId={`${testId}-button-confirm`}
                      label={t("global:confirm")}
                      size="medium"
                      width="220"
                      hierarchy="primary"
                      disabled={invalid}
                      onClick={onClick}
                    />
                  </div>
                </form>
              );
            }}
          </Form>
        </div>
      </div>
    </Modal>
  );
};
